import React, { useEffect, useState } from "react";
import useTranslation from "next-translate/useTranslation";
import Link from "next/link";
import { NextSeo, ArticleJsonLd } from "next-seo";

import {
  Box,
  Flex,
  Heading,
  Text,
  Link as ChakraLink,
  Divider,
  useMediaQuery,
  Spinner,
  Center,
} from "@chakra-ui/react";

import PostCardNoImg from "@/components/PostCardNoImg";
import SubscribeBlogBlock from "@/components/SubscribeBlogBlock";
import BlogLatestPosts from "@/components/BlogLatestPosts";
import Footer from "@/components/Footer";
import SubscribeBlock from "@/components/SubscribeBlock";
import ShareButtons from "@/components/ShareButtons";
import { formatDate } from "@/helpers/formatDate";

import { CategoryData } from "@/Types/types";
import { configFile } from "@/config";
import Breadcrumbs from "../Breadcrumbs";

import AdsCard from "../AdsCard";
import AdsCardHorizontal from "../AdsCardHorizontal";
import TableOfContent from "../TableOfContent";
import TopicsTable from "../TopicsTable";
import Image from "next/image";
import dynamic from "next/dynamic";
import AdsMobile from "../AdsMobile";

const AuthorCard = dynamic(() => import('@/components/AuthorCard'));

const PostContentRender = dynamic(() => import('@/components/PostContentRender'));


export default function PostPage(props: any) {

  const Comments = dynamic(() => import('@/components/Comments'), {
    ssr: false,
    loading: () => <Center w={'100%'} p={'16px'}><Spinner /></Center>
  });
  const [isMobileScreen] = useMediaQuery("(max-width: 990px)");
  const [isSmallMobileScreen] = useMediaQuery("(max-width: 320px)");
  const { t, lang } = useTranslation("common");

  const postCategories: Record<"attributes", CategoryData>[] =
    props.post.data[0].attributes.category.data;
  const topics: Record<"attributes", CategoryData>[] = props.topics.data;

  const date = formatDate(props.post.data[0].attributes.updatedAt, lang)

  const scrollToHeaders = () => {
    const ArticleWrapper = document.querySelector("article");
    const SecodLevelTitles = ArticleWrapper?.querySelectorAll("h2");

    SecodLevelTitles?.forEach((item) => {
      item.setAttribute("id", item.innerText.replace(/\s/g, ""));
    });
  };

  useEffect(() => {
    scrollToHeaders();
  }, [props]);


  return (
    <>
      <NextSeo
        title={props.post.data[0].attributes.title}
        description={props.post.data[0].attributes.metaDescription}
        canonical={
          props.post.data[0].attributes.canonicalURL &&
          props.post.data[0].attributes.canonicalURL
        }
        openGraph={{
          title: props.post.data[0].attributes.metaTitle,
          description: props.post.data[0].attributes.metaDescription,
          url: `${process.env.NEXT_PUBLIC_SITE_URL}/${props.post.data[0].attributes.url}`,
          type: "article",
          article: {
            publishedTime: props.post.data[0].attributes.updatedAt,
            authors: [
              props.post.data[0].attributes.author.data &&
              `${process.env.NEXT_PUBLIC_SITE_URL}/perfil/${props.post.data[0].attributes.author.data.attributes.url}`,
            ],
          },
          images: [
            {
              url: props.post.data[0].attributes.mainImage.data.attributes.url,
              alt: props.post.data[0].attributes.metaTitle,
            },
          ],
        }}
      />
      <ArticleJsonLd
        url={`${process.env.NEXT_PUBLIC_SITE_URL}/${props.post.data[0].attributes.url}`}
        title={props.post.data[0].attributes.metaTitle}
        images={[props.post.data[0].attributes.mainImage.data.attributes.url]}
        datePublished={props.post.data[0].attributes.updatedAt}
        authorName={[
          {
            name:
              props.post.data[0].attributes.author.data &&
              props.post.data[0].attributes.author.data.attributes.name,
            url:
              props.post.data[0].attributes.author.data &&
              `${process.env.NEXT_PUBLIC_SITE_URL}/perfil/${props.post.data[0].attributes.author.data.attributes.url}`,
          },
        ]}
        description={props.post.data[0].attributes.metaDescription}
        isAccessibleForFree={true}
      />
      <Box
        maxWidth="1216px"
        margin="0 auto"
        width="85%"
        mb="32px"
        as="header"
        pt={"32px"}
      >
        <Breadcrumbs data={props.breadcrumbs} />
        <Heading mb="20px" as="h1" fontWeight="800">
          {props.post.data[0].attributes.title}
        </Heading>
        <Flex
          columnGap="8px"
          rowGap="8px"
          flexWrap="wrap"
          mb={{ base: "60px", lg: "80px" }}
        >
          {props.post.data[0].attributes.author.data && (
            <>
              <Text color="gray.500" fontSize="16px" lineHeight="24px">
                {t("article.subtitle")}&nbsp;
                <ChakraLink 
                  prefetch={false}
                  as={Link}
                  fontWeight="600"
                  _hover={{
                    textDecor: "none",
                    color: configFile.primaryAccentColor,
                  }}
                  href={`perfil/${props.post.data[0].attributes.author.data.attributes.url}`}
                >
                  {props.post.data[0].attributes.author.data.attributes.name}
                </ChakraLink>
              </Text>
              <Text color="gray.500" fontSize="16px" lineHeight="24px">
                •
              </Text>
            </>
          )}
          {postCategories.length && (
            <Text color="gray.500" fontSize="16px" lineHeight="24px">
              {t("article.publishedIn")}{" "}
              {postCategories.map(({ attributes: category }, index) => (
                <React.Fragment key={index}>
                  <ChakraLink prefetch={false}
                    as={Link}
                    fontWeight="600"
                    href={`categoria/${category?.slug}`}
                    _hover={{
                      textDecoration: "none",
                      color: category?.color,
                    }}
                  >
                    {category?.title}
                  </ChakraLink>
                  {index !== postCategories.length - 1 && ", "}
                </React.Fragment>
              ))}
            </Text>
          )}
          <Text color="gray.500" fontSize="16px" lineHeight="24px">
            •
          </Text>
          <Text color="gray.500" fontSize="16px" lineHeight="24px">
            {date}
          </Text>
          <Text fontSize="16px" lineHeight="24px" color="red.500">
            •
          </Text>
          <Text
            fontSize="16px"
            lineHeight="24px"
            fontWeight="600"
            color="red.500"
          >
            {props.post.data[0].attributes.readingTime} {t("article.minRead")}
          </Text>
        </Flex>
        <Flex
          columnGap="64px"
          alignItems="flex-start"
          flexDir={{ base: "column", lg: "row" }}
        >
          <Box maxWidth={{ base: "100%", md: "100%", lg: "67%" }}>
            <Box
              bgColor="#fff"
              as="article"
              maxWidth="100%"
              overflow="hidden"
              boxShadow="0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06);"
              borderRadius="8px"
            >
              <Box
                width="100%"
                position="relative"
                mb="32px"
                height={{ base: "300px", lg: "400px" }}
              >
                <Image
                  src={
                    isMobileScreen ? 
                    props.post.data[0].attributes.mainImage.data.attributes.formats.small.url :
                    props.post.data[0].attributes.mainImage.data.attributes.url
                  }
                  fill
                  style={{
                    objectFit: "cover",
                  }}
                  alt={props.post.data[0].attributes.title}
                  priority={true}
                  sizes="(max-width: 768px) 75vw, 768px"
                />
              </Box>
              {!isMobileScreen && (
                <AdsCard
                  slot={"7468640258"}
                  format="fluid"
                  layout="in-article"
                  h={"280px"}
                  style={{
                    display: "inline-block",
                    width: "100%",
                    height: "280px",
                  }}
                />
              )}
              {isMobileScreen && (
                <AdsCard
                  slot={"2646007653"}
                  format={"inline-block"}
                  h={"250px"}
                  style={{
                    display: "inline-block",
                    width: "250px",
                    height: "250px",
                  }}
                />
              )}
              {/* <Box className="article" padding="0 32px 0 32px">
                {props.post.data[0].attributes.content &&
                  renderPostSubtitleAds()}
              </Box> */}
              {props.post.data[0].attributes.content &&
                <PostContentRender content={props.post.data[0].attributes.content} isMobileScreen={isMobileScreen} isSmallMobileScreen={isSmallMobileScreen} />
              }
              <Divider
                mt="32px"
                width="calc(100% - 64px)"
                display="block"
                bgColor="#CBD5E0"
                margin="0 auto"
              />
              <Flex
                mt="40px"
                mb="40px"
                padding="0 32px 0 32px"
                justifyContent="space-between"
                columnGap="32px"
                rowGap="20px"
                flexDirection={{ base: "column", md: "row" }}
              >
                <Flex
                  columnGap="4px"
                  alignItems="center"
                  flexWrap="wrap"
                  rowGap="20px"
                >
                  {props.post.data[0].attributes.tags.data.length > 0 && (
                    <Text
                      marginRight="8px"
                      fontSize="14px"
                      fontWeight="600"
                      color="gray.500"
                    >
                      Tags
                    </Text>
                  )}
                  {props.post.data[0].attributes.tags.data &&
                    props.post.data[0].attributes.tags.data.map(
                      (item: any, i: number) => (
                        <ChakraLink prefetch={false}
                          key={i}
                          as={Link}
                          padding="8px 12px"
                          borderRadius="8px"
                          bgColor="gray.100"
                          _hover={{
                            bgColor: configFile.fifthAccentColor,
                            color: configFile.tertiaryAccentColor,
                          }}
                          color="gray.500"
                          fontWeight="500"
                          fontSize="12px"
                          href={`tag/${item.attributes.url}`}
                        >
                          #{item.attributes.tag}
                        </ChakraLink>
                      )
                    )}
                </Flex>
                <ShareButtons url={props.post.data[0].attributes.url} />
              </Flex>

              <Comments
                id={props.post.data[0].id}
                item={props.comments && props.comments}
              />
              <Box padding="5px">
                <Flex
                  bgColor="gray.200"
                  columnGap="32px"
                  rowGap="32px"
                  borderBottomLeftRadius="8px"
                  borderBottomRightRadius="8px"
                  padding="27px 27px 43px 27px"
                  justifyContent="space-between"
                >
                  <Box flex="1 2 100%">
                    <Text
                      color="gray.500"
                      fontSize={{ base: "13", md: "16" }}
                      lineHeight="24px"
                      fontWeight="400px"
                    >
                      {t("article.previousArticle")}
                    </Text>
                    <ChakraLink prefetch={false}
                      as={Link}
                      _hover={{ textDecor: "none", color: "gray.800" }}
                      color="gray.600"
                      fontWeight="600"
                      fontSize={{ base: "13", md: "16" }}
                      marginTop="12px"
                      display="block"
                      href={
                        props.prevArticle.data[0]?.attributes.url ||
                        props.safeArticleOne.data[0].attributes.url
                      }
                    >
                      {props.prevArticle.data[0]?.attributes.title ||
                        props.safeArticleOne.data[0].attributes.title}
                    </ChakraLink>
                  </Box>
                  <Box textAlign="right" flex="1 2 100%">
                    <Text
                      color="gray.500"
                      fontSize={{ base: "13", md: "16" }}
                      lineHeight="24px"
                      fontWeight="400px"
                    >
                      {t("article.nextArticle")}
                    </Text>
                    <ChakraLink prefetch={false}
                      as={Link}
                      _hover={{ textDecor: "none", color: "gray.800" }}
                      color="gray.600"
                      fontWeight="600"
                      fontSize={{ base: "13", md: "16" }}
                      marginTop="12px"
                      display="block"
                      href={
                        props.nextArticle.data[0]?.attributes.url ||
                        props.safeArticleTwo.data[0].attributes.url
                      }
                    >
                      {props.nextArticle.data[0]?.attributes.title ||
                        props.safeArticleTwo.data[0].attributes.title}
                    </ChakraLink>
                  </Box>
                </Flex>
              </Box>
            </Box>
            <Box>
              <AdsCardHorizontal
                slot={"6188962576"}
                format={"auto"}
                h={"auto"}
                w={"100%"}
                layout="in-article"
                style={{
                  display: "block",
                  width: "100%",
                  height: "auto",
                }}
              />
            </Box>
          </Box>
          <Box
            maxWidth={{ base: "100%", md: "100%", lg: "33%" }}
            as="aside"
            display="flex"
            flexDir="column"
            rowGap="64px"
            mt={{ base: "40px", lg: "0" }}
          >
            {props.post.data[0].attributes.author.data && (
              <AuthorCard props={props} />
            )}
            {Array.from(
              props.post.data[0].attributes.content.matchAll(/^## (.*)$/gm)
            ).length > 0 && (
                <TableOfContent props={props} />
              )}
            {!isMobileScreen && (
              <AdsCard slot={"5656865130"} format={"auto"} h={"auto"} />
            )}
            {isMobileScreen && (
              <AdsCardHorizontal
                slot={"7554015132"}
                format={"auto"}
                h={"auto"}
                style={{
                  display: "inline-block",
                  width: "300px",
                  height: "100px",
                }}
              />
            )}
            <TopicsTable topics={topics} />
            <SubscribeBlogBlock />
            <Box w={{ md: '100%', lg: '384px' }} minH={'2px'}>
              {props.post.data[0].attributes.relatedArticles.data.length > 0 && (
                <>
                  <Text fontWeight="700" fontSize="18px" mb="12px">
                    {t("relatedPosts.title")}
                  </Text>
                  <Flex width="100%" flexDirection="column" rowGap="12px">
                    {props.post.data[0].attributes.relatedArticles.data.map(
                      (item: any, i: number) => (
                        <PostCardNoImg key={i} item={item} />
                      )
                    )}
                  </Flex>
                </>
              )}
            </Box>
          </Box>
        </Flex>
      </Box>
      {isMobileScreen && <AdsMobile postCategories={postCategories} />}
      <BlogLatestPosts posts={props.categoryPosts.data} />
      <SubscribeBlock />
      <Footer menu={props.footer[0].items} />
    </>
  );
}
